/* ==========================================================================
   rc-dealer-locator.js
   GAME Apparel Dealer Locator — v2 "map is the star" layout.

   - Compact search chip expands into a panel (desktop dropdown on hover/click,
     mobile bottom sheet behind a scrim).
   - Categorization pills filter the map, ZIP results, and Canada list.
   - Active filters render as removable mini chips.
   - "Need Help?" card expands to the Report / Request form-modal buttons.

   Data: #rc-dealer-data (inline JSON, public fields only).
   ZIP3 centroid table is fetched lazily from data-zip3-url on first ZIP search.
   No external dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var STATE_NAMES = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
    CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
    HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
    KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
    MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
    MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
    NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
    OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
    SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
    VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
    DC: 'District of Columbia'
  };

  var PHONE_ICON =
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6.6 10.8a15.5 15.5 0 0 0 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.2.4 2.5.6 3.8.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1A17 17 0 0 1 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.6.6 3.8.1.4 0 .8-.2 1z"/></svg>';

  var mqDesktop = window.matchMedia('(min-width: 751px)');

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Title-case a SCREAMING-CAPS string while keeping short tokens sensible.
  function tc(s) {
    if (!s) return '';
    return String(s).toLowerCase().replace(/\b([a-z])/g, function (m, c) {
      return c.toUpperCase();
    }).replace(/\.(Com|Net|Org)\b/g, function (m, t) {
      // Domain-style dealer names (USVARSITYJACKET.COM) keep a lowercase TLD.
      return '.' + t.toLowerCase();
    });
  }

  function haversine(lat1, lng1, lat2, lng2) {
    var R = 3958.8; // miles
    var dLat = (lat2 - lat1) * Math.PI / 180;
    var dLng = (lng2 - lng1) * Math.PI / 180;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function dealerCardHTML(d, distance) {
    var line2 = [tc(d.city), d.state, d.zip].filter(Boolean).join(', ').replace(', ' + d.zip, ' ' + d.zip);
    var addr = [esc(tc(d.addr1)), d.addr2 ? esc(tc(d.addr2)) : '', esc(line2)]
      .filter(Boolean).map(function (l) { return '<span>' + l + '</span>'; }).join('<br>');
    var html = '<div class="rc-dealer-card">';
    html += '<h3 class="rc-dealer-card__name">' + esc(tc(d.name)) + '</h3>';
    html += '<address class="rc-dealer-card__addr">' + addr + '</address>';
    if (typeof distance === 'number' && isFinite(distance)) {
      html += '<span class="rc-dealer-card__dist">' + Math.round(distance) + ' mi away</span><br>';
    }
    if (d.phone) {
      var tel = d.phone.replace(/[^0-9+]/g, '');
      html += '<a class="rc-dealer-card__phone" href="tel:' + esc(tel) + '">' + PHONE_ICON + esc(d.phone) + '</a>';
    }
    html += '</div>';
    return html;
  }

  function initSection(root) {
    var dataEl = root.querySelector('#rc-dealer-data');
    if (!dataEl) return;

    var dealers;
    try { dealers = JSON.parse(dataEl.textContent); }
    catch (e) { return; }

    var usDealers = dealers.filter(function (d) { return d.region === 'us'; });
    var caDealers = dealers.filter(function (d) { return d.region === 'ca'; });

    // Group US dealers by state abbreviation.
    var byState = {};
    usDealers.forEach(function (d) {
      (byState[d.state] = byState[d.state] || []).push(d);
    });

    var svg = root.querySelector('.rc-dealer-map__svg');
    var hint = root.querySelector('[data-rc-map-hint]');
    var zip3 = null;          // lazy-loaded centroid table
    var zip3Loading = null;   // in-flight promise

    /* ---- Categorization filter ------------------------------------------ */
    var currentCat = 'all';
    var catBtns = Array.prototype.slice.call(root.querySelectorAll('[data-rc-cat]'));

    function matchesCat(d) {
      if (currentCat === 'all') return true;
      if (d.cat === currentCat) return true;
      // Full Line dealers carry everything — they count as Workwear AND Varsity.
      return d.cat === 'fullline' && currentCat !== 'fullline';
    }
    function catFilter(list) { return list.filter(matchesCat); }
    function catLabel() {
      for (var i = 0; i < catBtns.length; i++) {
        if (catBtns[i].getAttribute('data-rc-cat') === currentCat) return catBtns[i].textContent.trim();
      }
      return '';
    }
    function stateCount(abbr) {
      return catFilter(byState[abbr] || []).length;
    }

    /* ---- Search chip + panel -------------------------------------------- */
    var chipWrap = root.querySelector('[data-rc-chipwrap]');
    var chip = root.querySelector('[data-rc-chip]');
    var chipZip = root.querySelector('[data-rc-chip-zip]');
    var chipCount = root.querySelector('[data-rc-chip-count]');
    var panel = root.querySelector('[data-rc-panel]');
    var scrim = root.querySelector('[data-rc-scrim]');
    var chipPlaceholder = chipZip ? chipZip.textContent : '';

    // activeZip: last successfully searched ZIP; activeZipCount: its result count.
    var activeZip = null;
    var activeZipCount = null;
    var panelOpen = false;
    var hoverCloseTimer = null;
    var panelHandle = panel ? panel.querySelector('.rc-dealer__panel-handle') : null;

    // Scrim + handle visibility is driven inline from JS: app-injected CSS on
    // this store has been observed interfering with plain class-based display
    // toggles (see PROGRESS 2026-07-21 card-gap saga), and inline styles win.
    function setScrimVisible(v) {
      if (!scrim) return;
      scrim.hidden = !v;
      scrim.style.display = v ? 'block' : 'none';
    }

    function updateChip() {
      if (!chip) return;
      if (chipZip) {
        chipZip.textContent = activeZip || chipPlaceholder;
        chipZip.classList.toggle('is-placeholder', !activeZip);
      }
      if (chipCount) {
        if (activeZip && activeZipCount != null) {
          chipCount.textContent = activeZipCount + ' dealer' + (activeZipCount === 1 ? '' : 's') + ' found';
        } else {
          var total = catFilter(dealers).length;
          chipCount.textContent = total + ' dealer' + (total === 1 ? '' : 's');
        }
      }
    }

    function openPanel() {
      if (!panel || panelOpen) return;
      panelOpen = true;
      panel.hidden = false;
      if (chip) chip.setAttribute('aria-expanded', 'true');
      if (!mqDesktop.matches) {
        setScrimVisible(true);
        if (panelHandle) panelHandle.style.display = 'block';
        document.body.classList.add('rc-dealer-sheet-open');
      }
      document.addEventListener('keydown', onPanelKeydown);
    }

    function closePanel() {
      if (!panel || !panelOpen) return;
      panelOpen = false;
      panel.hidden = true;
      if (chip) chip.setAttribute('aria-expanded', 'false');
      setScrimVisible(false);
      if (panelHandle) panelHandle.style.display = '';
      document.body.classList.remove('rc-dealer-sheet-open');
      document.removeEventListener('keydown', onPanelKeydown);
    }

    function onPanelKeydown(e) {
      if (e.key === 'Escape') closePanel();
    }

    if (chip && panel) {
      chip.addEventListener('click', function () {
        if (panelOpen) closePanel(); else openPanel();
      });

      // Desktop: hovering the chip (or the open panel) keeps it expanded.
      chipWrap.addEventListener('mouseenter', function () {
        if (!mqDesktop.matches) return;
        clearTimeout(hoverCloseTimer);
        openPanel();
      });
      chipWrap.addEventListener('mouseleave', function () {
        if (!mqDesktop.matches) return;
        clearTimeout(hoverCloseTimer);
        hoverCloseTimer = setTimeout(closePanel, 300);
      });

      // Click-away closes (scrim covers mobile; this covers desktop).
      document.addEventListener('click', function (e) {
        if (panelOpen && !chipWrap.contains(e.target)) closePanel();
      });
      if (scrim) scrim.addEventListener('click', closePanel);

      // Keep state sane when the viewport crosses the breakpoint.
      var onMq = function () { closePanel(); };
      if (mqDesktop.addEventListener) mqDesktop.addEventListener('change', onMq);
      else if (mqDesktop.addListener) mqDesktop.addListener(onMq);
    }

    /* ---- Active filter chips -------------------------------------------- */
    var activeWrap = root.querySelector('[data-rc-active]');
    var activeChips = root.querySelector('[data-rc-active-chips]');

    function renderActiveChips() {
      if (!activeWrap || !activeChips) return;
      activeChips.innerHTML = '';
      var any = false;

      if (activeZip) {
        any = true;
        var zc = document.createElement('button');
        zc.type = 'button';
        zc.className = 'rc-dealer__active-chip';
        zc.innerHTML = 'ZIP: ' + esc(activeZip) + ' <span aria-hidden="true">&times;</span>';
        zc.setAttribute('aria-label', 'Remove ZIP filter ' + activeZip);
        zc.addEventListener('click', function () {
          activeZip = null;
          activeZipCount = null;
          setMsg('', null);
          var input = root.querySelector('input[name="zip"]');
          if (input) input.value = '';
          updateChip();
          renderActiveChips();
        });
        activeChips.appendChild(zc);
      }

      if (currentCat !== 'all') {
        any = true;
        var cc = document.createElement('button');
        cc.type = 'button';
        cc.className = 'rc-dealer__active-chip';
        cc.innerHTML = esc(catLabel()) + ' <span aria-hidden="true">&times;</span>';
        cc.setAttribute('aria-label', 'Remove dealer type filter ' + catLabel());
        cc.addEventListener('click', function () { setCat('all'); });
        activeChips.appendChild(cc);
      }

      activeWrap.hidden = !any;
    }

    /* ---- Modal --------------------------------------------------------- */
    var modal = root.querySelector('[data-rc-modal]');
    var modalBox = root.querySelector('[data-rc-modal-box]');
    var modalTitle = root.querySelector('[data-rc-modal-title]');
    var modalCount = root.querySelector('[data-rc-modal-count]');
    var modalGrid = root.querySelector('[data-rc-modal-grid]');
    var modalEmpty = root.querySelector('[data-rc-modal-empty]');
    var lastFocus = null;

    function openModal(titleHTML, countText, list, withDistance) {
      modalTitle.innerHTML = titleHTML;
      modalCount.textContent = countText || '';
      if (list.length) {
        modalGrid.innerHTML = list.map(function (item) {
          return withDistance ? dealerCardHTML(item.d, item.dist) : dealerCardHTML(item);
        }).join('');
        modalEmpty.hidden = true;
      } else {
        modalGrid.innerHTML = '';
        modalEmpty.hidden = false;
      }
      lastFocus = document.activeElement;
      modal.hidden = false;
      // Compensate for the scrollbar width before locking scroll, so removing the
      // scrollbar doesn't widen the viewport and shift centered content (e.g. footer).
      var sbw = window.innerWidth - document.documentElement.clientWidth;
      if (sbw > 0) document.body.style.paddingRight = sbw + 'px';
      document.body.classList.add('rc-dealer-modal-open');
      // Focus the dialog container (not the heading) so screen readers announce
      // the modal without leaving a visible focus box on the title text.
      modalBox.focus();
      document.addEventListener('keydown', onKeydown);
    }

    function closeModal() {
      modal.hidden = true;
      document.body.classList.remove('rc-dealer-modal-open');
      document.body.style.paddingRight = '';
      document.removeEventListener('keydown', onKeydown);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    function onKeydown(e) {
      if (e.key === 'Escape') { closeModal(); return; }
      if (e.key !== 'Tab') return;
      // Focus trap within the modal box.
      var focusables = modalBox.querySelectorAll('button, a[href], input, [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    Array.prototype.slice.call(root.querySelectorAll('[data-rc-modal-close]')).forEach(function (el) {
      el.addEventListener('click', closeModal);
    });

    // Close when clicking anywhere outside the modal box (overlay or padding area).
    if (modal) {
      modal.addEventListener('click', function (e) {
        if (modalBox && !modalBox.contains(e.target)) closeModal();
      });
    }

    /* ---- Map interaction ----------------------------------------------- */
    function showState(abbr) {
      var list = catFilter(byState[abbr] || []).slice().sort(function (a, b) {
        return a.name.localeCompare(b.name);
      });
      var name = STATE_NAMES[abbr] || abbr;
      var countText = list.length + (list.length === 1 ? ' dealer' : ' dealers');
      if (currentCat !== 'all') countText += ' — ' + catLabel();
      openModal('GAME Dealers in ' + esc(name), countText, list, false);
    }

    // States are bound once (if they have ANY dealers) and re-skinned whenever
    // the categorization filter changes — a state with zero matching dealers
    // drops back to the default grey and stops responding.
    var boundShapes = [];

    function refreshMap() {
      boundShapes.forEach(function (b) {
        var count = stateCount(b.abbr);
        b.shape.classList.toggle('rc-has-dealers', count > 0);
        if (count > 0) {
          b.shape.setAttribute('tabindex', '0');
          b.shape.setAttribute('role', 'button');
          b.shape.setAttribute('aria-label', STATE_NAMES[b.abbr] + ': ' + count + ' dealer' + (count === 1 ? '' : 's'));
        } else {
          b.shape.removeAttribute('tabindex');
          b.shape.removeAttribute('role');
          b.shape.removeAttribute('aria-label');
        }
      });
    }

    if (svg) {
      var shapes = Array.prototype.slice.call(svg.querySelectorAll('g.state > path'));
      var dc = svg.querySelector('circle.dc');
      if (dc) shapes.push(dc);

      shapes.forEach(function (shape) {
        var cls = (shape.getAttribute('class') || '').split(/\s+/);
        var abbr = null;
        for (var i = 0; i < cls.length; i++) {
          var up = cls[i].toUpperCase();
          if (STATE_NAMES[up]) { abbr = up; break; }
        }
        if (!abbr) return;
        shape.setAttribute('data-state', abbr);

        if (!(byState[abbr] || []).length) return; // no dealers in any category

        boundShapes.push({ shape: shape, abbr: abbr });

        shape.addEventListener('click', function () {
          if (stateCount(abbr)) showState(abbr);
        });
        shape.addEventListener('keydown', function (e) {
          if ((e.key === 'Enter' || e.key === ' ') && stateCount(abbr)) { e.preventDefault(); showState(abbr); }
        });
        shape.addEventListener('mouseenter', function () {
          if (!hint) return;
          var count = stateCount(abbr);
          if (!count) return;
          hint.textContent = STATE_NAMES[abbr] + ' — ' + count;
          hint.setAttribute('data-show', 'true');
        });
        shape.addEventListener('mousemove', function (e) {
          if (!hint) return;
          var r = svg.getBoundingClientRect();
          hint.style.left = (e.clientX - r.left) + 'px';
          hint.style.top = (e.clientY - r.top) + 'px';
        });
        shape.addEventListener('mouseleave', function () {
          if (hint) hint.removeAttribute('data-show');
        });
      });

      refreshMap();
    }

    /* ---- ZIP search ---------------------------------------------------- */
    var zipForm = root.querySelector('[data-rc-zip-form]');
    var zipMsg = root.querySelector('[data-rc-zip-msg]');
    var resultsN = parseInt(root.getAttribute('data-zip-results'), 10) || 20;
    var zip3Url = root.getAttribute('data-zip3-url');

    function loadZip3() {
      if (zip3) return Promise.resolve(zip3);
      if (zip3Loading) return zip3Loading;
      zip3Loading = fetch(zip3Url)
        .then(function (r) { if (!r.ok) throw new Error('zip data'); return r.json(); })
        .then(function (json) { zip3 = json; return zip3; });
      return zip3Loading;
    }

    function setMsg(text, state) {
      if (!zipMsg) return;
      zipMsg.textContent = text || '';
      if (state) zipMsg.setAttribute('data-state', state);
      else zipMsg.removeAttribute('data-state');
    }

    // Rank the catFilter'd US dealers around a ZIP and (optionally) show them.
    // Reused by the cat pills so an active ZIP's count stays truthful.
    function runZipSearch(zipRaw, showResults) {
      return loadZip3().then(function (table) {
        var c = table[zipRaw.slice(0, 3)];
        if (!c) {
          setMsg('We couldn’t locate that ZIP code. Try a nearby one or use the map.', 'error');
          return;
        }
        var ranked = catFilter(usDealers)
          .filter(function (d) { return d.lat != null && d.lng != null; })
          .map(function (d) { return { d: d, dist: haversine(c[0], c[1], d.lat, d.lng) }; })
          .sort(function (a, b) { return a.dist - b.dist; })
          .slice(0, resultsN);

        activeZip = zipRaw;
        activeZipCount = ranked.length;
        setMsg(ranked.length + ' dealers found near ' + zipRaw + '.', 'ok');
        updateChip();
        renderActiveChips();

        if (showResults) {
          closePanel();
          var countText = 'Showing the ' + ranked.length + ' closest dealers';
          if (currentCat !== 'all') countText += ' — ' + catLabel();
          openModal('Dealers Near <span class="rc-dealer__modal-zip">' + zipRaw + '</span>',
            countText,
            ranked, true);
        }
      }).catch(function () {
        setMsg('Sorry, ZIP search is temporarily unavailable. Please use the map.', 'error');
      });
    }

    if (zipForm) {
      zipForm.addEventListener('submit', function (e) {
        e.preventDefault();
        var input = zipForm.querySelector('input[name="zip"]');
        var raw = (input.value || '').replace(/[^0-9]/g, '');
        if (raw.length < 5) { setMsg('Please enter a valid 5-digit ZIP code.', 'error'); return; }
        setMsg('Searching…', null);
        runZipSearch(raw, true);
      });
    }

    /* ---- Canada dealers (button -> shared modal) ----------------------- */
    var canadaBtn = root.querySelector('[data-rc-canada-btn]');

    function refreshCanada() {
      if (canadaBtn) canadaBtn.hidden = !catFilter(caDealers).length;
    }

    if (canadaBtn && caDealers.length) {
      caDealers.sort(function (a, b) { return a.name.localeCompare(b.name); });
      refreshCanada();
      canadaBtn.addEventListener('click', function () {
        var heading = canadaBtn.getAttribute('data-heading') || 'Canadian Dealers';
        var list = catFilter(caDealers);
        var countText = list.length + (list.length === 1 ? ' dealer' : ' dealers');
        if (currentCat !== 'all') countText += ' — ' + catLabel();
        openModal(esc(heading), countText, list, false);
      });
    }

    /* ---- Categorization pills ------------------------------------------ */
    function setCat(cat) {
      if (cat === currentCat) return;
      currentCat = cat;
      catBtns.forEach(function (b) {
        var on = b.getAttribute('data-rc-cat') === cat;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      refreshMap();
      refreshCanada();
      if (activeZip) {
        // Re-rank the active ZIP under the new filter (no modal pop).
        runZipSearch(activeZip, false);
      } else {
        updateChip();
        renderActiveChips();
      }
    }

    catBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        setCat(btn.getAttribute('data-rc-cat') || 'all');
      });
    });

    updateChip();
    renderActiveChips();

    /* ---- "Need Help?" card --------------------------------------------- */
    var help = root.querySelector('[data-rc-help]');
    var helpToggle = root.querySelector('[data-rc-help-toggle]');
    var helpBody = root.querySelector('[data-rc-help-body]');

    if (help && helpToggle && helpBody) {
      var helpCloseTimer = null;

      function setHelpOpen(open) {
        clearTimeout(helpCloseTimer);
        helpBody.hidden = !open;
        help.classList.toggle('is-open', open);
        helpToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      }

      helpToggle.addEventListener('click', function () {
        setHelpOpen(helpBody.hidden);
      });

      // Auto-close: hovering away for a few seconds…
      help.addEventListener('mouseenter', function () {
        clearTimeout(helpCloseTimer);
      });
      help.addEventListener('mouseleave', function () {
        if (helpBody.hidden) return;
        clearTimeout(helpCloseTimer);
        helpCloseTimer = setTimeout(function () { setHelpOpen(false); }, 3000);
      });

      // …or clicking/tapping anywhere outside the card.
      document.addEventListener('click', function (e) {
        if (!helpBody.hidden && !help.contains(e.target)) setHelpOpen(false);
      });
    }

    /* ---- Report / Request form modals ----------------------------------
       Same overlay/scroll-lock/focus-trap behavior as the results modal,
       but each form modal owns its own open/close pair. The modal body is
       a Powerful Form Builder embed rendered from section settings.       */
    Array.prototype.slice.call(root.querySelectorAll('[data-rc-form-open]')).forEach(function (openBtn) {
      var key = openBtn.getAttribute('data-rc-form-open');
      var formModal = root.querySelector('[data-rc-form-modal="' + key + '"]');
      if (!formModal) return;
      var formBox = formModal.querySelector('[data-rc-form-box]');
      var formLastFocus = null;

      function formKeydown(e) {
        if (e.key === 'Escape') { closeForm(); return; }
        if (e.key !== 'Tab') return;
        var f = formBox.querySelectorAll('button, a[href], input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])');
        if (!f.length) return;
        var first = f[0];
        var last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
      function openForm() {
        formLastFocus = document.activeElement;
        formModal.hidden = false;
        var sbw = window.innerWidth - document.documentElement.clientWidth;
        if (sbw > 0) document.body.style.paddingRight = sbw + 'px';
        document.body.classList.add('rc-dealer-modal-open');
        formBox.focus();
        document.addEventListener('keydown', formKeydown);
      }
      function closeForm() {
        formModal.hidden = true;
        document.body.classList.remove('rc-dealer-modal-open');
        document.body.style.paddingRight = '';
        document.removeEventListener('keydown', formKeydown);
        if (formLastFocus && formLastFocus.focus) formLastFocus.focus();
      }

      openBtn.addEventListener('click', openForm);
      Array.prototype.slice.call(formModal.querySelectorAll('[data-rc-form-close]')).forEach(function (el) {
        el.addEventListener('click', closeForm);
      });
      formModal.addEventListener('click', function (e) {
        if (formBox && !formBox.contains(e.target)) closeForm();
      });
    });
  }

  function boot() {
    document.querySelectorAll('[data-rc-dealer]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
